import { useEffect, useRef, useState, useCallback } from 'react';

const CHECK_INTERVAL_MS = 2 * 60 * 1000; // 2 minutos
const VERSION_URL = '/version.json';

/**
 * Detecta quando uma nova versão do frontend está disponível.
 * Faz polling em /version.json (gerado no prebuild com o git SHA) e
 * compara com a versão carregada no mount. Quando muda, sinaliza pro
 * UpdateBanner aparecer pedindo pro usuário recarregar.
 *
 * Retorna:
 * - hasUpdate: true quando a versão do servidor é diferente da inicial
 * - reload: força reload limpando cache
 */
export function useVersionCheck() {
  const [hasUpdate, setHasUpdate] = useState(false);
  const initialVersion = useRef(null);

  const fetchVersion = useCallback(async () => {
    try {
      const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data?.version || null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      const current = await fetchVersion();
      if (cancelled || !current) return;

      if (initialVersion.current === null) {
        initialVersion.current = current;
        return;
      }
      if (current !== initialVersion.current) {
        setHasUpdate(true);
      }
    };

    tick();
    const id = setInterval(tick, CHECK_INTERVAL_MS);
    const onFocus = () => tick();
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchVersion]);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  return { hasUpdate, reload };
}
